import { trpc } from '../trpc'
import type { RouterOutputs } from '../trpc'

export type SessionUser = RouterOutputs['auth']['me']

export type UseSessionResult = {
  user: SessionUser | undefined
  isLoading: boolean
  isAuthenticated: boolean
}

/**
 * Reads the current session from `auth.me` (cookie-based).
 * The tRPC refresh wrapper handles 401s transparently; `auth.me` only settles
 * to `error` once the silent refresh has ALSO failed — i.e. the session is
 * genuinely gone.
 *
 * Two states matter to the guard:
 *   - `isLoading` (status 'pending'): the first `auth.me` is still in flight and
 *     we don't yet know if the session is valid → show loading, never redirect.
 *   - `isError`: the query errored (expired session, refresh failed). TanStack
 *     Query keeps the last-successful `data` across a failed refetch, so we must
 *     gate `isAuthenticated` on the error flag too — otherwise a mid-session
 *     expiry leaves stale `user` in place and every downstream query throws
 *     visible errors instead of the guard redirecting to a clean login.
 */
export const useSession = (): UseSessionResult => {
  const { data: user, isPending, isError } = trpc.auth.me.useQuery(undefined, {
    // Don't retry on 401 — the refresh wrapper already tried once
    retry: false,
  })

  return {
    user,
    isLoading: isPending,
    isAuthenticated: !isError && user !== undefined,
  }
}
